// src/store/slices/post.slice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { postController } from '../../controllers/post.controller'

/* ---------------------------------------------------------- */
/* ------------------ Async Thunks -------------------------- */
/* ---------------------------------------------------------- */

// שליפת כל הפוסטים
export const fetchPosts = createAsyncThunk(
  'post/fetchPosts',
  async (filterBy = {}, { rejectWithValue }) => {
    try {
      return await postController.fetchPosts(filterBy)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// שליפת פוסט לפי מזהה
export const fetchPostById = createAsyncThunk(
  'post/fetchPostById',
  async (postId, { rejectWithValue }) => {
    try {
      return await postController.fetchPostById(postId)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// יצירת פוסט חדש
export const createPost = createAsyncThunk(
  'post/createPost',
  async (postData, { rejectWithValue, getState }) => {
    try {
      const loggedInUser = getState().userModule.loggedInUser
      return await postController.createPost(postData, loggedInUser)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// עריכת פוסט קיים
export const editPost = createAsyncThunk(
  'post/editPost',
  async ({ postId, updatedData }, { rejectWithValue, getState }) => {
    try {
      const loggedInUser = getState().userModule.loggedInUser
      return await postController.editPost(postId, updatedData, loggedInUser)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// מחיקת פוסט
export const removePost = createAsyncThunk(
  'post/removePost',
  async (postId, { rejectWithValue, getState }) => {
    try {
      const loggedInUser = getState().userModule.loggedInUser
      await postController.removePost(postId, loggedInUser)
      return postId
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// לייק/אנלייק לפוסט
export const toggleLike = createAsyncThunk(
  'post/toggleLike',
  async ({ postId }, { rejectWithValue, getState }) => {
    try {
      const userId = getState().userModule.loggedInUser?._id
      return await postController.toggleLike(postId, userId)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

// סימון Helpful (כן/לא)
export const updateHelpful = createAsyncThunk(
  'post/updateHelpful',
  async ({ postId, type }, { rejectWithValue, getState }) => {
    try {
      const loggedInUser = getState().userModule.loggedInUser
      return await postController.updateHelpful(postId, type, loggedInUser)
    } catch (err) {
      return rejectWithValue(err.message)
    }
  }
)

/* ---------------------------------------------------------- */
/* ---------------------- Slice ----------------------------- */
/* ---------------------------------------------------------- */

const initialState = {
  posts: [],
  selectedPost: null,
  isLoading: false,
  error: null,
}

const postSlice = createSlice({
  name: 'post',
  initialState,
  reducers: {
    addPostDirect(state, action) {
      const newPost = action.payload
      if (!state.posts.find((p) => p._id === newPost._id)) {
        state.posts.push(newPost)
      }
    },
    updatePostDirect(state, action) {
      const updatedPost = action.payload
      const idx = state.posts.findIndex((p) => p._id === updatedPost._id)
      if (idx !== -1) {
        state.posts[idx] = updatedPost
      }
      if (state.selectedPost?._id === updatedPost._id) {
        state.selectedPost = updatedPost
      }
    },
    removePostDirect(state, action) {
      const postId = action.payload
      state.posts = state.posts.filter((p) => p._id !== postId)
      if (state.selectedPost?._id === postId) {
        state.selectedPost = null
      }
    },
    setSelectedPost(state, action) {
      state.selectedPost = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      /* 🔹 שליפת כל הפוסטים */
      .addCase(fetchPosts.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        state.isLoading = false
        state.posts = action.payload
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      /* 🔹 שליפת פוסט בודד */
      .addCase(fetchPostById.fulfilled, (state, action) => {
        const post = action.payload
        state.selectedPost = post
        const idx = state.posts.findIndex((p) => p._id === post._id)
        if (idx !== -1) {
          state.posts[idx] = post
        } else {
          state.posts.push(post)
        }
      })
      .addCase(fetchPostById.rejected, (state, action) => {
        state.selectedPost = null
        state.error = action.payload
      })

      /* 🔹 יצירת פוסט */
      .addCase(createPost.fulfilled, (state, action) => {
        const newPost = action.payload
        if (!state.posts.some((post) => post._id === newPost._id)) {
          state.posts.push(newPost)
        }
      })

      /* 🔹 עדכון לייק */
      .addCase(toggleLike.pending, (state, action) => {
        const { postId, userId } = action.meta.arg
        const idx = state.posts.findIndex((p) => p._id === postId)
        if (idx !== -1 && userId) {
          const isLiked = state.posts[idx].likes.includes(userId)
          state.posts[idx].likes = isLiked
            ? state.posts[idx].likes.filter((id) => id !== userId)
            : [...state.posts[idx].likes, userId]
        }
        if (state.selectedPost?._id === postId && userId) {
          const isLiked = state.selectedPost.likes.includes(userId)
          state.selectedPost.likes = isLiked
            ? state.selectedPost.likes.filter((id) => id !== userId)
            : [...state.selectedPost.likes, userId]
        }
      })
      .addCase(toggleLike.fulfilled, (state, action) => {
        const { _id, likes } = action.payload
        const idx = state.posts.findIndex((p) => p._id === _id)
        if (idx !== -1) {
          state.posts[idx].likes = likes
        }
        if (state.selectedPost?._id === _id) {
          state.selectedPost.likes = likes
        }
      })
      
      

      /* 🔹 עדכון Helpful */
      .addCase(updateHelpful.fulfilled, (state, action) => {
        const updatedPost = action.payload
        const idx = state.posts.findIndex((p) => p._id === updatedPost._id)
        if (idx !== -1) {
          state.posts[idx] = updatedPost
        }
        if (state.selectedPost?._id === updatedPost._id) {
          state.selectedPost = updatedPost
        }
      })

      /* 🔹 מחיקת פוסט */
      .addCase(removePost.fulfilled, (state, action) => {
        const postId = action.payload
        state.posts = state.posts.filter((p) => p._id !== postId)
        if (state.selectedPost?._id === postId) {
          state.selectedPost = null
        }
      })
  },
})

/* ---------------------------------------------------------- */
/* -------------------- Exports ----------------------------- */
/* ---------------------------------------------------------- */

export const {
  addPostDirect,
  updatePostDirect,
  removePostDirect,
  setSelectedPost,
} = postSlice.actions

export const postReducer = postSlice.reducer
